import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Loader2,
  AlertCircle,
  CheckCircle,
  MapPin,
  ChevronDown,
  X,
  Phone,
  Star,
  Search,
  CheckSquare,
  Square,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/apiService";
import { authService } from "../services";
import LocationAutocomplete from "./ui/LocationAutocomplete";
import SuccessModal from "./ui/SuccessModal";
import ErrorModal from "./ui/ErrorModal";
import FleetDetailsCard from "./ui/FleetDetailsCard";
import {
  detectContactType,
  validatePhoneNumber,
  validateEmail,
} from "../utils/contactDetection";

// Debounce utility to prevent rapid state updates
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Helper function to check if form is complete
const isFormComplete = (name, phone, email, pickupLocation, dropLocation, pickupCoordinates, dropCoordinates, bookingType, date, time) => {
  return name.trim() &&
    (phone.trim() || email.trim()) &&
    pickupLocation.trim() &&
    dropLocation.trim() &&
    pickupCoordinates &&
    dropCoordinates &&
    (bookingType !== "scheduled" || (date && time));
};

// Helper function to validate phone number
const isValidPhone = (phone, isFleetDashboard = false, isHospitalDashboard = false) => {
  if (!phone.trim()) return true; // Phone is optional if email is provided
  const phoneDigits = phone.replace(/\D/g, "");
  
  // For fleet dashboard, require exactly 10 digits
  if (isFleetDashboard) {
    return phoneDigits.length === 10;
  }
  
  // For hospital dashboard, allow 1-10 digits (flexible international format)
  if (isHospitalDashboard) {
    return phoneDigits.length >= 1 && phoneDigits.length <= 10;
  }
  
  // For other users, use Indian phone number format (10 digits starting with 6-9)
  return phoneDigits.length === 10 && /^[6-9]\d{9}$/.test(phoneDigits);
};

// Helper function to validate contact information
const isValidContact = (phone, email, isFleetDashboard = false, isHospitalDashboard = false) => {
  if (phone.trim() && !isValidPhone(phone, isFleetDashboard, isHospitalDashboard)) return false;
  if (email.trim() && !validateEmail(email.trim())) return false;
  return true;
};

// Helper function to check if booking button should be disabled
const isBookingDisabled = (isLoading, name, phone, email, pickupCoordinates, dropCoordinates, bookingType, date, time, selectedAmbulance) => {
  return isLoading ||
    !name.trim() ||
    (!phone.trim() && !email.trim()) ||
    !pickupCoordinates ||
    !dropCoordinates ||
    (bookingType === "scheduled" && (!date || !time)) ||
    !selectedAmbulance;
};

// Helper function to clear contact fields
const clearContactFields = (setPhone, setEmail) => {
  setPhone("");
  setEmail("");
};

// Helper function to set email and clear phone
const setEmailAndClearPhone = (email, setPhoneFn, setEmailFn) => {
  setEmailFn(email);
  setPhoneFn("");
};

// Helper function to handle email input
const handleEmailInput = (trimmedInput, setPhone, setEmail) => {
  const detection = detectContactType(trimmedInput);
  if (detection.type === "email") {
    setEmailAndClearPhone(detection.email, setPhone, setEmail);
  } else {
    // Partial email being typed, allow it
    setEmailAndClearPhone(trimmedInput, setPhone, setEmail);
  }
};

// Helper function to validate and set phone number
const validateAndSetPhone = (restrictedDigits, setPhone, setEmail, isFleetDashboard = false, isHospitalDashboard = false) => {
  if (restrictedDigits.length === 0) {
    clearContactFields(setPhone, setEmail);
    return;
  }

  if (isFleetDashboard || isHospitalDashboard) {
    // For fleet dashboard or hospital dashboard, allow 1-10 digits with any first digit (international format)
    if (restrictedDigits.length >= 1 && restrictedDigits.length <= 10) {
      setPhone(restrictedDigits);
      setEmail("");
    }
    return;
  }

  // For other users, use Indian phone number format
  const firstDigit = restrictedDigits[0];
  const isValidSingleDigit = restrictedDigits.length === 1 && /^[6-9]$/.test(firstDigit);
  const isValidMultipleDigits = restrictedDigits.length > 1 && /^[6-9]\d*$/.test(restrictedDigits);

  if (isValidSingleDigit || isValidMultipleDigits) {
    setPhone(restrictedDigits);
    setEmail("");
  }
};

// Helper function to handle phone number input
const handlePhoneInput = (trimmedInput, setPhone, setEmail, isFleetDashboard = false, isHospitalDashboard = false) => {
  const digitsOnly = trimmedInput.replace(/\D/g, "");
  
  if (digitsOnly.length === 0) {
    // No digits found - check if it's a valid email or just invalid input
    const detection = detectContactType(trimmedInput);
    if (detection.type === "email") {
      setEmailAndClearPhone(detection.email, setPhone, setEmail);
    } else {
      clearContactFields(setPhone, setEmail);
    }
    return;
  }

  // For fleet dashboard or hospital dashboard, restrict to maximum 10 digits (international format)
  // For other users, restrict to maximum 10 digits (Indian format)
  const maxDigits = 10;
  const restrictedDigits = digitsOnly.slice(0, maxDigits);
  validateAndSetPhone(restrictedDigits, setPhone, setEmail, isFleetDashboard, isHospitalDashboard);
};

// Helper function to handle contact input change with validation
const handleContactChange = (input, setPhone, setEmail, isFleetDashboard = false, isHospitalDashboard = false) => {
  const trimmedInput = input.trim();
  
  // If input is empty, clear both
  if (!trimmedInput) {
    clearContactFields(setPhone, setEmail);
    return;
  }

  // Check if input contains @ (email indicator)
  if (trimmedInput.includes("@")) {
    handleEmailInput(trimmedInput, setPhone, setEmail);
    return;
  }

  // Handle phone number input
  handlePhoneInput(trimmedInput, setPhone, setEmail, isFleetDashboard, isHospitalDashboard);
};

// Helper function to handle fleet partner checkbox change
const handleFleetPartnerChange = (id, checked, selectedFleetPartners, setSelectedFleetPartners, preferredFleetId, setPreferredFleetId) => {
  if (checked) {
    const next = [...selectedFleetPartners, id];
    setSelectedFleetPartners(next);
    if (!preferredFleetId) setPreferredFleetId(id);
  } else {
    const next = selectedFleetPartners.filter((x) => x !== id);
    setSelectedFleetPartners(next);
    if (preferredFleetId === id) setPreferredFleetId(next[0] || null);
  }
};

// Dropdown button component for direction selection
const DirectionDropdownButton = ({ value, label, currentDirection, onSelect, onClose }) => (
  <button
    type="button"
    className={`w-full px-4 py-3 text-sm text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${
      currentDirection === value
        ? "bg-blue-50 text-blue-700"
        : "text-gray-700"
    }`}
    onClick={() => {
      onSelect(value);
      onClose();
    }}
  >
    <span>{label}</span>
    {currentDirection === value && (
      <CheckCircle className="w-4 h-4 text-blue-600" />
    )}
  </button>
);

// Dropdown button component for payment selection
const PaymentDropdownButton = ({ value, label, icon, currentPaymentType, onSelect, onClose }) => (
  <button
    type="button"
    className={`w-full px-4 py-3 text-sm text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${
      currentPaymentType === value
        ? "bg-blue-50 text-blue-700"
        : "text-gray-700"
    }`}
    onClick={() => {
      onSelect(value);
      onClose();
    }}
  >
    <span className="flex items-center gap-2">{icon} {label}</span>
    {currentPaymentType === value && (
      <CheckCircle className="w-4 h-4 text-blue-600" />
    )}
  </button>
);

const GetAmbulanceCard = ({
  onBookingChange,
  selectedAmbulance,
  triggerBooking,
  onPickupCoordinatesChange,
  onDropCoordinatesChange,
  fareEstimates,
  onShowChooseAmbulance,
  onFormDataChange,
  // Optional props for location features (used by duplicate flow)
  showMapPicker = false,
  showCurrentLocation = false,
}) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [paymentType, setPaymentType] = useState("Cash");
  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);
  const [bookingType, setBookingType] = useState("instant");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropLocation, setDropLocation] = useState("");
  const [direction, setDirection] = useState("Outbound");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFleetDropdownOpen, setIsFleetDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const paymentDropdownRef = useRef(null);
  const fleetDropdownRef = useRef(null);

  // Refs to store latest callbacks to avoid re-render loops
  const onFormDataChangeRef = useRef(onFormDataChange);
  const onShowChooseAmbulanceRef = useRef(onShowChooseAmbulance);

  // Update refs when callbacks change
  useEffect(() => {
    onFormDataChangeRef.current = onFormDataChange;
    onShowChooseAmbulanceRef.current = onShowChooseAmbulance;
  }, [onFormDataChange, onShowChooseAmbulance]);

  // Debounced direction change handler to prevent ResizeObserver issues
  const debouncedSetDirection = debounce(setDirection, 100);

  // Location coordinates state
  const [pickupCoordinates, setPickupCoordinates] = useState(null);
  const [dropCoordinates, setDropCoordinates] = useState(null);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        paymentDropdownRef.current &&
        !paymentDropdownRef.current.contains(event.target)
      ) {
        setIsPaymentDropdownOpen(false);
      }
      if (
        fleetDropdownRef.current &&
        !fleetDropdownRef.current.contains(event.target)
      ) {
        setIsFleetDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Modal states
  const [successModal, setSuccessModal] = useState({
    isOpen: false,
    data: null,
  });
  
  // Error modal state for mobile number validation
  const [mobileNumberErrorModal, setMobileNumberErrorModal] = useState({
    isOpen: false,
    message: "",
  });
  
  // Validation error states for scheduled booking
  const [dateError, setDateError] = useState("");
  const [timeError, setTimeError] = useState("");
  
  // Get user context for role-based features
  const { user, isAuthenticated } = useAuth();
  
  // Determine if this is fleet dashboard (fleet operators)
  const isFleetDashboard = isAuthenticated && (user?.role === "TENANT_ADMIN" || user?.role === "TENANT_USER");
  
  // Determine if this is hospital dashboard (hospital users)
  const isHospitalDashboard = isAuthenticated && (user?.role === "HOSPITAL_USER" || user?.role === "hospital" || user?.role === "Hospital");
  
  // Helper: Validate mobile number is exactly 10 digits (Indian format)
  // For fleet dashboard users, require exactly 10 digits
  const validateMobileNumberInput = (phoneNumber) => {
    if (!phoneNumber || !phoneNumber.trim()) {
      // Empty is valid only if email is provided
      return { isValid: true, message: "" };
    }

    const phoneDigits = phoneNumber.replace(/\D/g, ""); // Remove all non-digits

    // For fleet dashboard users, require exactly 10 digits
    if (isFleetDashboard) {
      if (phoneDigits.length === 0) {
        return { isValid: true, message: "" }; // Empty is valid if email is provided
      }
      if (phoneDigits.length < 10) {
        return {
          isValid: false,
          message: "Mobile number is incomplete. Please enter a complete 10-digit mobile number."
        };
      }
      if (phoneDigits.length > 10) {
        return {
          isValid: false,
          message: "Mobile number is too long. Please enter a valid 10-digit mobile number."
        };
      }
      // Exactly 10 digits is valid for fleet users
      return { isValid: true, message: "" };
    }

    // Skip strict validation for hospital dashboard users (they can use email)
    if (isHospitalDashboard) {
      return { isValid: true, message: "" };
    }

    // For other users, must be exactly 10 digits starting with 6-9
    if (phoneDigits.length !== 10) {
      return {
        isValid: false,
        message: "Please enter a valid 10-digit mobile number"
      };
    }

    // Must start with 6, 7, 8, or 9 (Indian mobile number format)
    if (!/^[6-9]\d{9}$/.test(phoneDigits)) {
      return {
        isValid: false,
        message: "Please enter a valid 10-digit mobile number starting with 6, 7, 8, or 9"
      };
    }

    return { isValid: true, message: "" };
  };

  // Handle mobile number blur validation
  const handleMobileNumberBlur = () => {
    // Skip validation for hospital dashboard users (they can use email)
    if (isHospitalDashboard) {
      return;
    }
    // Validate if phone is entered (not email)
    if (phone && phone.trim() && !email) {
      const validation = validateMobileNumberInput(phone);
      if (!validation.isValid) {
        setMobileNumberErrorModal({
          isOpen: true,
          message: validation.message,
        });
      }
    }
  };
  
  // Partner fleets state (for hospital users)
  const [partnerFleets, setPartnerFleets] = useState([]);
  const [loadingPartnerFleets, setLoadingPartnerFleets] = useState(false);
  const [selectedFleetPartners, setSelectedFleetPartners] = useState([]);
  const [preferredFleetId, setPreferredFleetId] = useState(null);
  const [fleetFilterText, setFleetFilterText] = useState("");
  
  // Partner hospitals state (for fleet operators)
  const [partnerHospitals, setPartnerHospitals] = useState([]);
  const [loadingPartnerHospitals, setLoadingPartnerHospitals] = useState(false);
  const [selectedHospitalId, setSelectedHospitalId] = useState(null);
  const [hospitalFilterText, setHospitalFilterText] = useState("");
  
  // Removed confirmationModal - no longer needed with auto-show ambulances

  // Note: triggerBooking is handled by the parent component now

  // Auto-show ambulances when form is complete
  useEffect(() => {
    // Clear previous messages
    setError("");

    // Check if all required fields are filled
    if (!isFormComplete(name, phone, email, pickupLocation, dropLocation, pickupCoordinates, dropCoordinates, bookingType, date, time)) {
      return; // Form not complete, don't show ambulances
    }

    // Validate contact information
    if (!isValidContact(phone, email, isFleetDashboard, isHospitalDashboard)) {
      return; // Invalid contact, don't show ambulances
    }

    // For fleet dashboard users, validate mobile number if provided
    // Block navigation to next step if mobile number is incomplete or invalid
    if (isFleetDashboard && phone && phone.trim() && !email) {
      const phoneValidation = validateMobileNumberInput(phone);
      if (!phoneValidation.isValid) {
        // Show error modal and block navigation
        setMobileNumberErrorModal({
          isOpen: true,
          message: phoneValidation.message,
        });
        return; // Don't proceed to next step
      }
    }

    // All validations passed, prepare form data and show ambulances
    const cleanedPhone = phone.trim() ? phone.replace(/\D/g, "") : "";

    const formData = {
      name: name.trim(),
      phone: cleanedPhone,
      email: email.trim(),
      paymentType: paymentType,
      bookingType: bookingType,
      pickupLocation: pickupLocation.trim(),
      dropLocation: dropLocation.trim(),
      direction: direction,
      date: date,
      time: time,
      // Fleet partner selection (for hospital users)
      fleetPartnerIds: selectedFleetPartners,
      preferredFleetId: preferredFleetId || undefined,
      // Hospital partner selection (for fleet operators)
      hospitalPartnerId: selectedHospitalId || undefined,
    };

    console.log("📋 Form auto-complete detected, showing ambulances:", {
      hasPhone: !!cleanedPhone,
      hasEmail: !!email.trim(),
      phone: cleanedPhone || "(not provided)",
      email: email.trim() || "(not provided)",
    });

    // Send form data to parent component using ref
    if (onFormDataChangeRef.current) {
      onFormDataChangeRef.current(formData);
    }

    // Auto-show ChooseAmbulance card using ref with small delay to prevent immediate API overload
    if (onShowChooseAmbulanceRef.current) {
      setTimeout(() => {
        onShowChooseAmbulanceRef.current();
      }, 500); // 500ms delay to allow location setting to complete
    }
  }, [
    name,
    phone,
    email,
    pickupLocation,
    dropLocation,
    pickupCoordinates,
    dropCoordinates,
    bookingType,
    date,
    time,
    direction,
    paymentType,
    selectedFleetPartners,
    preferredFleetId,
    selectedHospitalId,
    isFleetDashboard,
    isHospitalDashboard,
  ]);

  // Fetch partner fleets (for hospital users) - Shows all mapped partner fleets
  useEffect(() => {
    if (isAuthenticated && (user?.role === "HOSPITAL_USER" || user?.role === "SUPER_ADMIN")) {
      const fetchPartnerFleets = async () => {
        try {
          setLoadingPartnerFleets(true);
          const response = await apiService.get('/hospital/partner-fleets?status=active');
          if (response?.success && Array.isArray(response.data)) {
            setPartnerFleets(response.data);
          }
        } catch (e) {
          console.error('❌ [FLEET PARTNERS] Error fetching mapped partner fleets:', e);
          // Silent fail; selection is optional
        } finally {
          setLoadingPartnerFleets(false);
        }
      };
      fetchPartnerFleets();
    }
  }, [isAuthenticated, user?.role]);

  // Auto-select all partner fleets for hospital users (since UI is removed)
  useEffect(() => {
    if (partnerFleets.length > 0 && isAuthenticated && (user?.role === "HOSPITAL_USER" || user?.role === "SUPER_ADMIN")) {
      // Extract all fleet IDs from partner fleets
      const allFleetIds = partnerFleets.map(fleet => 
        String(fleet.id || fleet.fleet_id || fleet.operator_id)
      ).filter(id => id && id !== 'undefined');
      
      // Only update if the selected fleets are different
      const currentIds = [...selectedFleetPartners].sort();
      const newIds = [...allFleetIds].sort();
      if (allFleetIds.length > 0 && JSON.stringify(currentIds) !== JSON.stringify(newIds)) {
        console.log("✅ [FLEET PARTNERS] Auto-selecting all partner fleets:", allFleetIds);
        setSelectedFleetPartners(allFleetIds);
        // Set the first fleet as preferred if none is set
        if (!preferredFleetId && allFleetIds.length > 0) {
          setPreferredFleetId(allFleetIds[0]);
        }
      }
    }
  }, [partnerFleets, isAuthenticated, user?.role, selectedFleetPartners, preferredFleetId]);

  // Debug: Log when partner fleets change
  useEffect(() => {
    if (partnerFleets.length > 0) {
      console.log("✅ [FLEET PARTNERS] Partner fleets state updated:", partnerFleets.length, "fleets");
      console.log("📋 [FLEET PARTNERS] Fleet data:", partnerFleets);
    }
  }, [partnerFleets]);

  // Fetch partner hospitals (for fleet operators) - Shows all hospitals for partnership selection
  useEffect(() => {
    if (isAuthenticated && (user?.role === "TENANT_ADMIN" || user?.role === "TENANT_USER")) {
      const fetchPartnerHospitals = async () => {
        try {
          setLoadingPartnerHospitals(true);
          console.log("🏥 [HOSPITAL PARTNERS] Fetching hospitals for partnership selection...");
          
          // Call the new API endpoint
          const response = await authService.fleetFetchHospitals();
          console.log("🏥 [HOSPITAL PARTNERS] API Response:", response);
          
          let hospitals = [];
          
          // Handle response structure: { success: true, data: { hospitals: [...] } }
          if (response?.success && response?.data?.hospitals && Array.isArray(response.data.hospitals)) {
            hospitals = response.data.hospitals.map(hospital => {
              // Parse address to extract city/state if needed
              const addressParts = hospital.address ? hospital.address.split(',') : [];
              const city = addressParts.length > 0 ? addressParts[0].trim() : '';
              const state = addressParts.length > 1 ? addressParts[addressParts.length - 1].trim() : '';
              
              return {
                id: hospital.hospital_id,
                hospital_id: hospital.hospital_id,
                name: hospital.name,
                hospital_name: hospital.name,
                address: hospital.address || '',
                phone: hospital.phone || null,
                latitude: hospital.latitude,
                longitude: hospital.longitude,
                is_partner: hospital.is_partner || false,
                // Map API fields to expected component fields
                city: city,
                state: state,
                contact_person_phone: hospital.phone || null,
                contact_phone: hospital.phone || null,
                status: hospital.is_partner ? 'active' : 'inactive'
              };
            });
            console.log("✅ [HOSPITAL PARTNERS] Loaded", hospitals.length, "hospitals from API");
            console.log("📋 [HOSPITAL PARTNERS] Hospital names:", hospitals.map(h => h.name || h.hospital_name || 'Unknown'));
            console.log("📋 [HOSPITAL PARTNERS] Partner hospitals:", hospitals.filter(h => h.is_partner).length);
          } else {
            console.warn("⚠️ [HOSPITAL PARTNERS] Unexpected response structure:", response);
          }
          
          // Set hospitals
          setPartnerHospitals(hospitals);
          
          if (hospitals.length === 0) {
            console.warn("⚠️ [HOSPITAL PARTNERS] No hospitals found");
          }
        } catch (e) {
          console.error('❌ [HOSPITAL PARTNERS] Error fetching hospitals:', e);
          // Silent fail; selection is optional
        } finally {
          setLoadingPartnerHospitals(false);
        }
      };
      fetchPartnerHospitals();
    }
  }, [isAuthenticated, user?.role]);

  // Handle confirmed booking submission
  const handleConfirmBooking = async (bookingData) => {
    console.log("=== BOOKING CONFIRMATION STARTED ===");
    console.log("Booking data received:", bookingData);

    // Validate mobile number before submitting
    if (phone && phone.trim() && !email) {
      const validation = validateMobileNumberInput(phone);
      if (!validation.isValid) {
        setMobileNumberErrorModal({
          isOpen: true,
          message: validation.message,
        });
        return; // Stop booking submission if mobile number is invalid
      }
    }
    
    // Validate scheduled booking date and time
    if (bookingType === "scheduled") {
      if (!date || !date.trim()) {
        setDateError("Date is required for scheduled bookings");
        setMobileNumberErrorModal({
          isOpen: true,
          message: "Please select a date for scheduled booking",
        });
        return;
      }
      if (!time || !time.trim()) {
        setTimeError("Time is required for scheduled bookings");
        setMobileNumberErrorModal({
          isOpen: true,
          message: "Please select a time for scheduled booking",
        });
        return;
      }
    }

    setIsLoading(true);

    try {
      console.log("Submitting booking data:", bookingData);

      // Call the API
      const result = await apiService.createBooking(bookingData);

      console.log("Booking created successfully:", result);

      // Extract booking ID
      const bookingId =
        result?.data?.booking_id ||
        result?.data?.id ||
        result?.booking_id ||
        result?.id;

      if (bookingId) {
        // Show success modal
        // Build scheduled date time for display
        let scheduledDateTimeForDisplay = undefined;
        if (bookingType === "scheduled" && date && time) {
          scheduledDateTimeForDisplay = new Date(`${date}T${time}`).toISOString();
        }
        
        setSuccessModal({
          isOpen: true,
          data: {
            bookingId,
            patientName: bookingData.patientName || name,
            hospitalName: "Selected Hospital", // Default since we don't have hospital info in this component
            ambulanceType: bookingData.ambulanceType || selectedAmbulance?.name,
            estimatedFare:
              fareEstimates[selectedAmbulance?.type_id] || "Calculating...",
            bookingType: bookingType === "scheduled" ? "SCHEDULED" : "INSTANT",
            scheduledDateTime: scheduledDateTimeForDisplay,
            ...bookingData,
            success: true,
            result,
          },
        });

        // Call the callback with booking data
        if (onBookingChange) {
          onBookingChange({
            bookingId,
            ...bookingData,
            success: true,
            result,
          });
        }

        // Reset form after successful booking (but keep modal open for user to close manually)
        setTimeout(() => {
          setName("");
          setPhone("");
          setEmail("");
          setPickupLocation("");
          setDropLocation("");
          setPickupCoordinates(null);
          setDropCoordinates(null);
          setDate("");
          setTime("");
          setError("");
          // Removed automatic modal close - let user close it manually
        }, 3000);
      } else {
        throw new Error("No booking ID received from server");
      }
    } catch (error) {
      console.error("Booking creation failed:", error);

      // Handle different error types
      if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("Failed to create booking. Please try again.");
      }
    } finally {
      setIsLoading(false);
      console.log("=== BOOKING CONFIRMATION COMPLETED ===");
    }
  };

  return (
    <>
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 w-full max-w-lg h-fit self-start">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <svg
              className="w-6 h-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">
            Book Ambulance
          </h2>
          <p className="text-sm text-gray-500">
            Quick & reliable medical transport
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="text-red-700 text-sm font-medium">{error}</div>
          </div>
        )}

        {/* Form Fields */}
        <div className="space-y-4">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Patient Name
            </label>
            <input
              type="text"
              placeholder="Enter patient name"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Smart Contact Field */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
            Mobile Number
            </label>
            <input
              type="text"
              placeholder="Enter contact number"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
              value={phone || email || ""}
              onChange={(e) => {
                const inputValue = e.target.value;
                // Always validate and update state to keep input and state in sync
                handleContactChange(inputValue, setPhone, setEmail, isFleetDashboard, isHospitalDashboard);
              }}
              onBlur={handleMobileNumberBlur}
              onKeyPress={(e) => {
                // Prevent invalid characters at input level (works for typing)
                const currentValue = e.target.value || "";
                const isEmailMode = currentValue.includes("@") || email;
                
                if (!isEmailMode) {
                  // For phone mode, validate input
                  const key = e.key;
                  
                  // Only allow digits
                  if (!/^\d$/.test(key)) {
                    e.preventDefault();
                    return;
                  }
                  
                  const currentDigits = (phone || "").replace(/\D/g, "");
                  
                  if (isFleetDashboard || isHospitalDashboard) {
                    // For fleet dashboard or hospital dashboard, allow 1-10 digits (flexible international format)
                    if (currentDigits.length >= 10) {
                      e.preventDefault();
                      return;
                    }
                    // Allow any digit (0-9) as first digit for international numbers
                  } else {
                    // For other users, check if already at 10 digits (Indian format)
                    if (currentDigits.length >= 10) {
                      e.preventDefault();
                      return;
                    }
                    
                    // Check if this would be the first digit - must be 6-9 for Indian numbers
                    if (currentDigits.length === 0 && !/^[6-9]$/.test(key)) {
                      e.preventDefault();
                      return;
                    }
                  }
                }
              }}
              onPaste={(e) => {
                // Handle paste events to validate pasted content
                e.preventDefault();
                const pastedText = e.clipboardData.getData("text");
                handleContactChange(pastedText, setPhone, setEmail, isFleetDashboard, isHospitalDashboard);
                // Validate after paste
                setTimeout(() => {
                  if (pastedText && !pastedText.includes("@") && !email) {
                    const validation = validateMobileNumberInput(pastedText);
                    if (!validation.isValid) {
                      setMobileNumberErrorModal({
                        isOpen: true,
                        message: validation.message,
                      });
                    }
                  }
                }, 0);
              }}
              maxLength={email ? undefined : 10}
            />
            <p className="text-xs text-gray-500 mt-1">
              Enter contact number
            </p>
          </div>

          {/* Journey Direction */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Journey Direction
            </label>
            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white text-left flex items-center justify-between"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                <span className="text-gray-700">{direction}</span>
                <ChevronDown
                  className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                    isDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              </button>

              {isDropdownOpen && (
                <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                  <button
                    type="button"
                    className={`w-full px-4 py-3 text-sm text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${
                      direction === "Outbound"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                    onClick={() => {
                      debouncedSetDirection("Outbound");
                      setIsDropdownOpen(false);
                    }}
                  >
                    <span>Outbound</span>
                    {direction === "Outbound" && (
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                    )}
                  </button>
                  <button
                    type="button"
                    className={`w-full px-4 py-3 text-sm text-left hover:bg-blue-50 transition-colors duration-200 flex items-center justify-between ${
                      direction === "Inbound"
                        ? "bg-blue-50 text-blue-700"
                        : "text-gray-700"
                    }`}
                    onClick={() => {
                      debouncedSetDirection("Inbound");
                      setIsDropdownOpen(false);
                    }}
                  >
                    <span>Inbound</span>
                    {direction === "Inbound" && (
                      <CheckCircle className="w-4 h-4 text-blue-600" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Location Fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2 text-blue-600" />
                Pickup Location
              </label>
              <LocationAutocomplete
                value={pickupLocation}
                onChange={(e) => setPickupLocation(e.target.value)}
                onLocationSelect={(location) => {
                  setPickupCoordinates(location);
                  if (location && onPickupCoordinatesChange) {
                    onPickupCoordinatesChange(location);
                  }
                }}
                placeholder="Search pickup location..."
                required
                showMapPicker={showMapPicker}
                mapPickerTitle="Select Pickup Location"
                pinColor="#10B981"
                showCurrentLocation={showCurrentLocation}
                useFullAddress={isHospitalDashboard}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-2 text-blue-600" />
                Drop Location
              </label>
              <LocationAutocomplete
                value={dropLocation}
                onChange={(e) => setDropLocation(e.target.value)}
                onLocationSelect={(location) => {
                  setDropCoordinates(location);
                  if (location && onDropCoordinatesChange) {
                    onDropCoordinatesChange(location);
                  }
                }}
                placeholder="Search drop location..."
                required
                showMapPicker={showMapPicker}
                mapPickerTitle="Select Drop Location"
                pinColor="#EF4444"
                useFullAddress={isHospitalDashboard}
                suggestionsPlacement="above"
              />
            </div>
          </div>

          {/* Booking Type - Commented out because default booking type is already instant */}
          {/* 
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Booking Type
            </label>
            <div className="grid grid-cols-1 gap-3">
              <label
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                  bookingType === "instant"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 bg-gray-50 text-gray-600 hover:border-gray-300"
                }`}
              >
                <input
                  type="radio"
                  checked={bookingType === "instant"}
                  onChange={() => setBookingType("instant")}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm font-medium">Instant</span>
              </label>
            </div>
          </div>
          */}

          {/* Hospital Partner Selection (Optional) - For Fleet Operators */}
          {isAuthenticated && (user?.role === "TENANT_ADMIN" || user?.role === "TENANT_USER") && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Hospital Partner(s)
              </label>
              {loadingPartnerHospitals ? (
                <div className="flex items-center justify-center p-4 border-2 border-gray-200 rounded-xl">
                  <Loader2 className="w-4 h-4 animate-spin text-blue-600 mr-2" />
                  <span className="text-gray-600 text-sm">Loading partner hospitals...</span>
                </div>
              ) : partnerHospitals.length === 0 ? (
                <div className="p-4 text-center text-gray-500 border-2 border-gray-200 rounded-xl">
                  <p className="text-sm">No mapped partner hospitals found</p>
                </div>
              ) : (
                <>
                  {/* Filter Input */}
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search hospitals..."
                      value={hospitalFilterText}
                      onChange={(e) => setHospitalFilterText(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                    />
                  </div>

                  {/* Hospital List with Checkboxes */}
                  <div className="max-h-60 overflow-y-auto border-2 border-gray-200 rounded-xl p-2 space-y-2">
                    {partnerHospitals
                      .filter((hospital) => {
                        if (!hospitalFilterText) return true;
                        const searchText = hospitalFilterText.toLowerCase();
                        const hospitalName = (hospital.name || hospital.hospital_name || '').toLowerCase();
                        const location = (hospital.city || hospital.address || '').toLowerCase();
                        return hospitalName.includes(searchText) || location.includes(searchText);
                      })
                      .map((hospital) => {
                        const id = String(hospital.id || hospital.hospital_id);
                        const hospitalName = hospital.name || hospital.hospital_name || 'Hospital';
                        const location = hospital.city 
                          ? `${hospital.city}${hospital.state ? `, ${hospital.state}` : ''}`
                          : hospital.address || '';
                        const isSelected = selectedHospitalId === id;
                        
                        // Extract contact number (try multiple possible field names)
                        const contactNumber = hospital.contact_person_phone || 
                                             hospital.phone || 
                                             hospital.contact_number || 
                                             hospital.contact_phone || 
                                             '';
                        
                        // Extract BLS rate from partner_rates
                        let ratesInfo = null;
                        if (hospital.partner_rates) {
                          if (hospital.partner_rates.BLS) {
                            const bls = hospital.partner_rates.BLS;
                            const base = bls.base || bls;
                            const perKm = bls.per_km || bls;
                            if (typeof base === 'number' && typeof perKm === 'number') {
                              ratesInfo = `BLS ₹${base} + ₹${perKm}/km`;
                            }
                          } else if (Array.isArray(hospital.partner_rates)) {
                            const blsRate = hospital.partner_rates.find(r => r.type === 'BLS');
                            if (blsRate) {
                              ratesInfo = `BLS ₹${blsRate.base} + ₹${blsRate.per_km}/km`;
                            }
                          }
                        }
                        
                        return (
                          <label
                            key={id}
                            className={`relative flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-white hover:border-gray-300'
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              // Single selection: set the selected hospital (or deselect if clicking the same one)
                              setSelectedHospitalId((prevId) => prevId === id ? null : id);
                            }}
                          >
                            {/* Green Chip with "Partner" at Top-Right Corner - Only show if is_partner is true */}
                            {hospital.is_partner && (
                              <div className="absolute top-1.5 right-1.5 z-10 inline-flex items-center px-2 py-0.5 rounded-full bg-gradient-to-br from-green-50 to-green-100/80 border border-green-200/70 shadow-sm">
                                <span className="text-[10px] font-bold text-green-700/90 tracking-tight">
                                  Partner
                                </span>
                              </div>
                            )}
                            <div className="flex-shrink-0 mt-0.5">
                              {isSelected ? (
                                <CheckSquare className="w-5 h-5 text-blue-600" />
                              ) : (
                                <Square className="w-5 h-5 text-gray-400" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900 text-sm">{hospitalName}</span>
                                  </div>
                                  {location && (
                                    <p className="text-xs text-gray-500 mt-0.5">{location}</p>
                                  )}
                                  {contactNumber && (
                                    <div className="flex items-center gap-1 mt-1">
                                      <Phone className="w-3 h-3 text-gray-400" />
                                      <p className="text-xs text-gray-600">{contactNumber}</p>
                                    </div>
                                  )}
                                  {ratesInfo && (
                                    <p className="text-xs text-green-600 font-medium mt-1">{ratesInfo}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                            <input
                              type="radio"
                              checked={isSelected}
                              readOnly
                              className="sr-only"
                            />
                          </label>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          )}


          {/* Date & Time Fields - Commented Out (Scheduled Booking Only) */}
          {/* {bookingType === "scheduled" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={date}
                  onChange={(e) => {
                    setDate(e.target.value);
                    if (e.target.value) {
                      setDateError("");
                    }
                  }}
                  onBlur={() => {
                    if (bookingType === "scheduled" && !date) {
                      setDateError("Date is required for scheduled bookings");
                    } else {
                      setDateError("");
                    }
                  }}
                  className={`w-full border-2 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white ${
                    dateError ? "border-red-300" : "border-gray-200"
                  }`}
                  min={new Date().toISOString().split("T")[0]}
                  required={bookingType === "scheduled"}
                />
                {dateError && (
                  <p className="text-xs text-red-600 mt-1">{dateError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  value={time}
                  onChange={(e) => {
                    setTime(e.target.value);
                    if (e.target.value) {
                      setTimeError("");
                    }
                  }}
                  onBlur={() => {
                    if (bookingType === "scheduled" && !time) {
                      setTimeError("Time is required for scheduled bookings");
                    } else {
                      setTimeError("");
                    }
                  }}
                  className={`w-full border-2 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white ${
                    timeError ? "border-red-300" : "border-gray-200"
                  }`}
                  required={bookingType === "scheduled"}
                />
                {timeError && (
                  <p className="text-xs text-red-600 mt-1">{timeError}</p>
                )}
              </div>
            </div>
          )} */}
        </div>

        {/* Payment Type Selection - MOVED TO CHOOSE AMBULANCE CARD */}
        {/* <div className="mt-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Payment Method
          </label>
          <div className="relative" ref={paymentDropdownRef}>
            <button
              type="button"
              onClick={() => setIsPaymentDropdownOpen(!isPaymentDropdownOpen)}
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 hover:bg-white text-left flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                {paymentType === "Cash" && "💵 Cash"}
                {paymentType === "Online" && "💳 Online"}
              </span>
              <svg
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  isPaymentDropdownOpen ? "rotate-180" : ""
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {isPaymentDropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-200 rounded-xl shadow-lg overflow-hidden">
                <PaymentDropdownButton
                  value="Cash"
                  label="Cash"
                  icon="💵"
                  currentPaymentType={paymentType}
                  onSelect={setPaymentType}
                  onClose={() => setIsPaymentDropdownOpen(false)}
                />
                <PaymentDropdownButton
                  value="Online"
                  label="Online"
                  icon="💳"
                  currentPaymentType={paymentType}
                  onSelect={setPaymentType}
                  onClose={() => setIsPaymentDropdownOpen(false)}
                />
              </div>
            )}
          </div>
        </div> */}

        {/* Book Ambulance Button - MOVED TO CHOOSE AMBULANCE CARD */}
        {/* <button
          className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-4 rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-6 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-[1.02]"
          onClick={triggerBooking}
          disabled={isBookingDisabled(
            isLoading,
            name,
            phone,
            email,
            pickupCoordinates,
            dropCoordinates,
            bookingType,
            date,
            time,
            selectedAmbulance
          )}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              {selectedAmbulance
                ? `Book ${selectedAmbulance.name}`
                : "Book Ambulance"}
            </>
          )}
        </button> */}
      </div>

      {/* Modals */}
      {successModal.isOpen && (
        <SuccessModal
          isOpen={successModal.isOpen}
          onClose={() => setSuccessModal({ isOpen: false, data: null })}
          bookingId={successModal.data?.bookingId}
          patientName={successModal.data?.patientName}
          hospitalName={successModal.data?.hospitalName}
          ambulanceType={successModal.data?.ambulanceType}
          estimatedFare={successModal.data?.estimatedFare}
          contactPhone={successModal.data?.patientPhone}
          contactPhone_country_code={
            successModal.data?.patientPhone_country_code
          }
          contactEmail={successModal.data?.patientEmail}
          pickupAddress={successModal.data?.pickupLocation?.address}
          destinationAddress={successModal.data?.dropoffLocation?.address}
          bookingType={successModal.data?.bookingType}
          direction={successModal.data?.direction}
          scheduledDateTime={successModal.data?.scheduledDateTime}
          scheduledDate={date}
          scheduledTime={time}
        />
      )}

      {/* Mobile Number Validation Error Modal */}
      <ErrorModal
        isOpen={mobileNumberErrorModal.isOpen}
        onClose={() => {
          setMobileNumberErrorModal({
            isOpen: false,
            message: "",
          });
        }}
        title="Invalid Mobile Number"
        message={mobileNumberErrorModal.message || "Please enter a valid 10-digit mobile number"}
      />
    </>
  );
};

export default GetAmbulanceCard;
